/**
 * מסך "יצירת הפנייה חדשה"  —  /create-lead
 *
 * המסך הראשי של הרכזת. היא מקלידה כאן הפנייה שכללית שלחה: שם, תעודת זהות,
 * טלפון, מספר התחייבות (טופס 17), ואפשר לצרף את מסמך ההפנייה.
 *
 * "הפנייה" במסך = "lead" בקוד ובדאטהבייס. אותו דבר, שתי מילים - הטבלה באנגליה
 * נקראת leads, ולכן הקוד נשאר בשם הזה.
 *
 * המחוז לא נבחר כאן - הוא נגזר מהמשתמש המחובר, אחרת רכזת דן הייתה יכולה
 * לפתוח הפנייה בשם דרום.
 */
import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { createLead, uploadReferralFile, LeadPayload } from '../services/leadService';
import { getApiError } from '../utils/apiError';
import { formatDate } from '../utils/dateUtils';
import {
  isValidIsraeliId,
  isValidObligation,
  isValidIsraeliMobile,
} from '../utils/israeliValidation';

// One refusal used to cover four different situations, and the coordinator
// could not tell them apart: a referral she sent this morning, somebody already
// in treatment whose second obligation belongs on another screen entirely,
// somebody who declined and cannot be reopened from here, and an ID she
// mistyped that happens to belong to a real person. The status of the referral
// already on file says which, so each one gets the sentence that fits it and,
// where there is somewhere to go, says where.
//
// A mistyped ID needs no case of its own: she reads "filled the questionnaire
// on 27/08" about a referral she never sent, and checks the number.
const duplicateIdMessage = (detail: any): string => {
  const opened = detail?.existing_created_at
    ? formatDate(detail.existing_created_at)
    : '';
  const on = opened ? ` ב-${opened}` : '';

  switch (detail?.existing_status) {
    case 'CREATED':
      return `כבר נשלחה הודעה לאדם זה${on}. ממתינים שימלא את השאלון.`;
    case 'MATCHED':
      return `אדם זה כבר מילא את השאלון${on} ונמצא בטיפול. `
        + 'התחייבות המשך מוזנת במסך "אישורי המשך".';
    case 'NOT_INTERESTED':
      return `אדם זה סומן כלא מעוניין${on}. `
        + 'אם חזר בו, ניתן לבטל את הסימון במסך הלידים של הצוות.';
    default:
      return 'כבר קיימת הפנייה עבור תעודת הזהות הזו.';
  }
};

// The HMO shell is English LTR; this Clalit-facing screen is Hebrew, so the
// form is wrapped in its own dir="rtl" container.
const CreateLeadPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  // onTouched: a field stays quiet until she has left it once, and from then on
  // the error clears as she types the fix - she does not have to leave the field
  // again to see that it is now good.
  } = useForm<LeadPayload>({ mode: 'onTouched' });

  const [referralFile, setReferralFile] = useState<File | null>(null);
  const [serverWarnings, setServerWarnings] = useState<string[]>([]);
  const [createdLeadId, setCreatedLeadId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onSubmit = async (data: LeadPayload) => {
    try {
      const result = await createLead(data);
      if (referralFile) {
        await uploadReferralFile(result.lead.id, referralFile);
      }
      setServerWarnings(result.warnings);
      setCreatedLeadId(result.lead.id);
    } catch (err: any) {
      // A duplicate TZ is the one error a coordinator will hit routinely, and a
      // toast fades before she has read it - so it is pinned to the field that
      // caused it as well.
      if (err?.response?.status === 409) {
        const message = duplicateIdMessage(err?.response?.data?.detail);
        setError('id_number', { type: 'server', message });
        toast.error(message);
        return;
      }
      toast.error(getApiError(err, 'שגיאה ביצירת ההפנייה'));
    }
  };

  const startNew = () => {
    reset();
    setReferralFile(null);
    setServerWarnings([]);
    setCreatedLeadId(null);
  };

  if (createdLeadId !== null) {
    return (
      <Box dir="rtl" sx={{ maxWidth: 560, mx: 'auto' }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64 }} />
          <Typography variant="h5" sx={{ mt: 2 }}>
            ההפנייה נוצרה בהצלחה
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            המטופל יקבל הודעה עם קישור לשאלון
          </Typography>
          {serverWarnings.map((w) => (
            <Alert severity="warning" sx={{ mt: 2, textAlign: 'right' }} key={w}>
              {w}
            </Alert>
          ))}
          {/* Entering several referrals in a row is the ordinary thing, so that
              stays the primary action. The second closes the loop: the referral
              she just created is the first row on that screen, which is the only
              way from here to see that it landed. */}
          <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
            <Button variant="contained" size="large" onClick={startNew}>
              יצירת הפנייה חדשה
            </Button>
            <Button variant="outlined" size="large" onClick={() => navigate('/my-leads')}>
              ההפניות שלי
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box dir="rtl" sx={{ maxWidth: 560, mx: 'auto' }}>
      <Paper sx={{ p: { xs: 2, sm: 4 } }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, color: 'primary.dark', textAlign: 'center', mb: 3 }}
        >
          יצירת הפנייה חדשה
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={2}>
            <TextField
              label="שם פרטי"
              required
              fullWidth
              error={!!errors.first_name}
              helperText={errors.first_name?.message || ' '}
              {...register('first_name', { required: 'שדה חובה' })}
            />
            <TextField
              label="שם משפחה"
              required
              fullWidth
              error={!!errors.last_name}
              helperText={errors.last_name?.message || ' '}
              {...register('last_name', { required: 'שדה חובה' })}
            />
            <TextField
              label="תעודת זהות"
              required
              fullWidth
              inputProps={{ inputMode: 'numeric', maxLength: 9 }}
              error={!!errors.id_number}
              helperText={errors.id_number?.message || ' '}
              {...register('id_number', {
                required: 'שדה חובה',
                validate: (v) => isValidIsraeliId(v) || 'מספר תעודת זהות אינו תקין',
              })}
            />
            <TextField
              label="מספר התחייבות (טופס 17)"
              required
              fullWidth
              inputProps={{ inputMode: 'numeric', maxLength: 9 }}
              error={!!errors.obligation_number}
              helperText={errors.obligation_number?.message || ' '}
              {...register('obligation_number', {
                required: 'שדה חובה',
                validate: (v) =>
                  isValidObligation(v) || 'מספר התחייבות חייב להכיל 9 ספרות בדיוק',
              })}
            />
            <TextField
              label="טלפון נייד"
              required
              fullWidth
              inputProps={{ inputMode: 'tel' }}
              error={!!errors.phone}
              helperText={errors.phone?.message || ' '}
              {...register('phone', {
                required: 'שדה חובה',
                validate: (v) => isValidIsraeliMobile(v) || 'מספר נייד ישראלי אינו תקין',
              })}
            />
            <Box>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => setReferralFile(e.target.files?.[0] || null)}
              />
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                {referralFile ? referralFile.name : 'צירוף קובץ הפנייה'}
              </Button>
              {referralFile && (
                <Button
                  size="small"
                  color="error"
                  sx={{ mr: 1 }}
                  onClick={() => setReferralFile(null)}
                >
                  הסרה
                </Button>
              )}
            </Box>
            <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
              {isSubmitting ? 'שומר...' : 'יצירת הפנייה'}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default CreateLeadPage;
